/**
 * Health landing visual tokens — aligned with Graphen site components:
 * TitleSubtitle, TextBody, ToolCard2, StepsCard, MoreButton, ContactUsButton, MainFrame.
 */
import {
    healthContentPadX,
    healthSectionBlock,
    healthSectionYCompact,
} from "./healthLayout";

/** Site section backgrounds (MainFrame / Homepage) */
export const HEALTH_BG_GREY = "#F5F5F5";
export const HEALTH_BG_CARD = "#F8F9FA";

/** Lifecycle section surface — consistent across desktop / tablet / mobile */
export const HEALTH_BG_LIFECYCLE = "#F8F9FA";

/** Primary CTA band — mid-teal contact section background */
export const HEALTH_CTA_BAND_BG = "#3299A0";

/** Education hub problem band — mint tint (distinct from HEALTH_BG_GREY) */
export const EDUCATION_PROBLEM_BAND = "#eef7f8";

export const healthHeroLoopPanel =
    "relative h-full w-full overflow-hidden rounded-3xl border border-grey-100 bg-[radial-gradient(circle_at_70%_20%,rgba(87,196,203,0.15),transparent_45%),radial-gradient(circle_at_20%_40%,rgba(162,218,219,0.12),transparent_40%),#f8fbfc]";

/** Product hero visual — contain illustration inside soft panel (not solution crop frame) */
/* —— Health child / module-detail pages (e.g. Aimma Doctor) —— */

export const healthChildPageTitle =
    "text-left text-xl font-bold whitespace-pre-line text-grey-800 lg:text-2xl lg:leading-tight";

export const healthChildPageLead =
    "text-left text-base leading-relaxed text-grey-500 max-w-lg";

export const healthChildPageMetaLink =
    "text-sm font-medium text-teal-600 hover:text-teal-700";

export const healthChildPageHeroVisual =
    "relative w-full overflow-hidden rounded-3xl border border-grey-100/80 bg-[radial-gradient(circle_at_70%_20%,rgba(87,196,203,0.12),transparent_45%),#f8fbfc]";

export const healthChildPageHeroVisualInner =
    "flex w-full items-center justify-center p-5 sm:p-6 lg:p-8";

/** Hero screenshot — single-column keeps natural image ratio; frame crops the lower edge only. */
export const healthChildHeroProductImageFrame =
    "relative w-full max-lg:aspect-[16/10] max-lg:max-h-[min(56vw,300px)] max-lg:overflow-hidden sm:max-lg:max-h-[320px] lg:overflow-visible";

export const healthChildHeroProductImageGlow =
    "pointer-events-none absolute inset-x-[6%] top-[14%] -bottom-3 -z-10 rounded-[28px] bg-[radial-gradient(ellipse_at_center,rgba(87,196,203,0.2)_0%,rgba(87,196,203,0.06)_46%,transparent_74%)] blur-2xl";

export const healthChildHeroProductImage =
    "relative z-[1] block h-auto w-full max-w-full rounded-3xl shadow-[0_24px_64px_rgba(16,24,40,0.16)] max-lg:max-w-none lg:max-h-none";

export const healthChildCard =
    "w-full rounded-2xl bg-white px-5 py-5 text-left shadow-[0_8px_24px_rgba(16,24,40,0.06)] lg:px-6 lg:py-6";

/** UI mockup placeholder shell (agent, SOAP, band) */
export const healthChildProductPreviewFrame =
    "relative w-full overflow-hidden rounded-2xl border border-grey-100/80 bg-[radial-gradient(circle_at_70%_20%,rgba(87,196,203,0.1),transparent_45%),#f8fbfc]";

export const healthChildProductPreviewInner =
    "flex aspect-[16/10] w-full items-center justify-center p-4 sm:p-5";

export const healthChildProductPreviewInnerPrimary =
    "flex aspect-[16/10] w-full min-h-[220px] items-center justify-center p-4 sm:p-6 lg:min-h-[280px]";

/** Hero column — capped dashboard preview (Aimma Doctor) */
export const healthChildProductPreviewInnerHeroCompact =
    "flex aspect-[16/10] w-full max-h-[200px] items-center justify-center p-4 sm:max-h-[240px] lg:max-h-[280px]";

/** Small agent / oversight UI reference card */
/** Ecosystem flow strip (integration section) */
export const healthChildProductPreviewPlaceholder =
    "px-4 text-center text-xs font-medium uppercase tracking-wider text-grey-400";

/** Journey phase card */
/** SOAP explainer inset @deprecated — use HealthSoapWorkflowSection card grid */
/** SOAP workflow — four explanation cards */
export const healthSoapCardGrid =
    "grid w-full min-w-0 auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5";

export const healthSoapCardBase =
    "flex h-full min-h-0 w-full min-w-0 flex-col rounded-[1.25rem] border px-5 pb-5 pt-5 text-left shadow-[0_8px_24px_rgba(16,24,40,0.05)] lg:min-h-[13.5rem] lg:px-7 lg:pb-7 lg:pt-8 [text-align:left]";

export const healthSoapCardContent =
    "flex min-h-0 flex-1 flex-col";

export const healthSoapCardBadge =
    "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-lg font-bold tracking-tight lg:h-14 lg:w-14 lg:text-[1.65rem]";

export const healthSoapCardTitle =
    "mt-3 text-left text-base font-semibold leading-snug lg:mt-5 lg:text-lg [text-align:left]";

export const healthSoapCardDivider =
    "mt-3 h-px w-12 max-w-[42%] rounded-full lg:mt-3.5 lg:w-14";

export const healthSoapCardText =
    "mt-3 flex-1 text-left text-sm leading-[1.65] text-grey-600 lg:mt-4 [text-align:left]";

/** Decorative corner icon — lg+ only; SOAP letter badge is enough on smaller viewports */
export const healthSoapCardIconWrap =
    "mt-5 hidden shrink-0 items-end justify-end pt-2 lg:flex lg:mt-6 lg:pt-3";

export const healthSoapCardIcon =
    "h-6 w-6 sm:h-7 sm:w-7";

export const healthSoapCardThemeSubjective = {
    card: "border-teal-200/70 bg-[#eef9fa]",
    badge: "border-teal-200/70 bg-[#dff3f5] text-teal-700",
    title: "text-teal-700",
    divider: "bg-teal-300/40",
    icon: "text-teal-400/50",
};

export const healthSoapCardThemeObjective = {
    card: "border-sky-200/70 bg-[#eef6fc]",
    badge: "border-sky-200/70 bg-[#dceefb] text-sky-700",
    title: "text-sky-700",
    divider: "bg-sky-300/40",
    icon: "text-sky-400/50",
};

export const healthSoapCardThemeAssessment = {
    card: "border-violet-200/70 bg-[#f3f0fb]",
    badge: "border-violet-200/70 bg-[#e8e2f8] text-violet-700",
    title: "text-violet-700",
    divider: "bg-violet-300/40",
    icon: "text-violet-400/50",
};

export const healthSoapCardThemePlan = {
    card: "border-emerald-200/70 bg-[#eef8f3]",
    badge: "border-emerald-200/70 bg-[#dcf3e8] text-emerald-700",
    title: "text-emerald-700",
    divider: "bg-emerald-300/40",
    icon: "text-emerald-500/45",
};

/** Aimma Nurse coordination previews — shared surfaces and badges */
export const healthNursingCoordinationStepSurfaces = [
    "rounded-[1.15rem] bg-[#eef9fa] p-3 sm:p-3.5",
    "rounded-[1.15rem] bg-[#eef6fc] p-3 sm:p-3.5",
    "rounded-[1.15rem] bg-[#f3f0fb] p-3 sm:p-3.5",
] as const;

export const healthNursingCoordinationStepBadges = [
    "border-teal-200/70 bg-[#dff3f5] text-teal-700",
    "border-sky-200/70 bg-[#dceefb] text-sky-700",
    "border-violet-200/70 bg-[#e8e2f8] text-violet-700",
] as const;

export const healthNursingCoordinationSnippetMetaLabel =
    "text-left text-[10px] font-medium uppercase tracking-wide text-grey-500 [text-align:left]";

export const healthNursingCoordinationSnippetMetaValue =
    "text-left text-[11px] font-medium text-grey-700 [text-align:left]";

/** SOAP workflow — physician sign-off strip (border only, no fill) */
/** Physician Control — section rhythm */
export const healthPhysicianControlSectionStack =
    "mt-8 flex w-full min-w-0 flex-col gap-8 lg:mt-10 lg:gap-9";

/** Physician Control — governance panel */
export const healthPhysicianControlPanel =
    "grid w-full min-w-0 grid-cols-1 gap-0 overflow-hidden rounded-[1.5rem] border border-teal-100/45 bg-white shadow-[0_12px_40px_rgba(3,124,132,0.07)] lg:min-h-[24rem] lg:grid-cols-[minmax(0,0.37fr)_minmax(0,0.63fr)]";

export const healthPhysicianControlStatement =
    "flex h-full min-w-0 flex-col space-y-7 bg-[linear-gradient(165deg,rgba(207,236,238,0.72)_0%,rgba(232,247,248,0.94)_38%,rgba(252,254,254,1)_100%)] p-7 text-left sm:p-8 lg:min-h-[24rem] lg:justify-center lg:space-y-8 lg:rounded-l-[1.5rem] lg:border-r lg:border-teal-100/35 lg:p-10 lg:pr-9 [text-align:left]";

export const healthPhysicianControlStatementIcon =
    "flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-[#037C84]/20 bg-white text-[#037C84] shadow-[0_8px_22px_rgba(3,124,132,0.12)] lg:h-[4.75rem] lg:w-[4.75rem]";

export const healthPhysicianControlStatementTitle =
    "text-left text-xl font-bold leading-snug text-grey-900 lg:text-[1.75rem] lg:leading-tight [text-align:left]";

export const healthPhysicianControlStatementBody =
    "max-w-[24rem] text-left text-base leading-[1.7] text-grey-700 [text-align:left]";

export const healthPhysicianControlTrustPill =
    "inline-flex w-fit max-w-full items-center gap-2 rounded-full bg-[#dff3f5] px-4 py-2.5 text-left text-sm font-semibold leading-snug text-[#037C84] [text-align:left]";

export const healthPhysicianControlCards =
    "flex min-w-0 flex-col gap-4 p-6 sm:p-7 lg:justify-center lg:gap-4 lg:p-8 lg:pl-7";

export const healthPhysicianControlCard =
    "grid w-full min-w-0 grid-cols-1 items-start gap-3 rounded-[1.125rem] border border-teal-50/80 bg-white px-5 py-5 shadow-[0_4px_18px_rgba(16,24,40,0.04)] sm:gap-4 sm:px-6 sm:py-6 lg:min-h-[6.75rem] lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center lg:gap-6 lg:px-7";

export const healthPhysicianControlCardIndex =
    "flex h-[3.25rem] w-14 shrink-0 items-center justify-center self-start rounded-xl bg-[#eef9fa] text-base font-bold tracking-tight text-[#037C84] lg:h-[3.75rem] lg:w-16 lg:text-lg";

export const healthPhysicianControlCardCopy =
    "min-w-0 py-0.5 lg:pr-2";

export const healthPhysicianControlCardTitle =
    "text-left text-lg font-bold leading-snug text-grey-900 [text-align:left]";

export const healthPhysicianControlCardText =
    "mt-2 text-left text-[0.9375rem] leading-[1.6] text-grey-600 [text-align:left]";

export const healthPhysicianControlCardIcon =
    "h-9 w-9 shrink-0 self-start text-[#57aeb3] lg:h-10 lg:w-10";

/** Physician Control — control-before-release strip */
/** Physician Control — final governance note */
export const healthPhysicianControlNote =
    "flex w-full min-w-0 items-start gap-4 border-t border-grey-200/60 bg-transparent px-0.5 pt-7 text-left lg:pt-8 [text-align:left]";

export const healthPhysicianControlNoteIcon =
    "inline-flex h-[calc(0.9375rem*1.65)] shrink-0 items-center leading-none text-[#037C84]";

export const healthPhysicianControlNoteText =
    "min-w-0 flex-1 text-left text-[0.9375rem] leading-[1.65] text-grey-600 [text-align:left]";

/** Integration Ecosystem — section rhythm */
export const healthIntegrationEcosystemSectionStack =
    "mt-8 flex w-full min-w-0 flex-col gap-8 lg:mt-10 lg:gap-9";

/** Themed linked-card hover — shared across integration, product suite, secondary tiles */
export type HealthLinkedCardTheme = "teal" | "sky" | "amber" | "violet";

const healthLinkedCardHoverBorders: Record<HealthLinkedCardTheme, string> = {
    teal: "border-grey-100/80 ring-1 ring-transparent hover:border-teal-300/65 hover:ring-teal-100/70",
    sky: "border-grey-100/80 ring-1 ring-transparent hover:border-sky-300/70 hover:ring-sky-100/70",
    amber: "border-grey-100/80 ring-1 ring-transparent hover:border-amber-300/70 hover:ring-amber-100/70",
    violet:
        "border-grey-100/80 ring-1 ring-transparent hover:border-violet-300/70 hover:ring-violet-100/70",
};

const healthLinkedCardHoverShadows: Record<HealthLinkedCardTheme, string> = {
    teal: "hover:shadow-[0_8px_28px_rgba(16,24,40,0.07),0_32px_80px_rgba(3,124,132,0.22)]",
    sky: "hover:shadow-[0_8px_28px_rgba(16,24,40,0.07),0_32px_80px_rgba(14,116,144,0.2)]",
    amber: "hover:shadow-[0_8px_28px_rgba(16,24,40,0.07),0_32px_80px_rgba(217,119,6,0.18)]",
    violet: "hover:shadow-[0_8px_28px_rgba(16,24,40,0.07),0_32px_80px_rgba(91,33,182,0.16)]",
};

const healthLinkedCardFocusOutlines: Record<HealthLinkedCardTheme, string> = {
    teal: "focus-visible:outline-teal-500",
    sky: "focus-visible:outline-sky-500",
    amber: "focus-visible:outline-amber-500",
    violet: "focus-visible:outline-violet-500",
};

const healthLinkedCardTextHovers: Record<HealthLinkedCardTheme, string> = {
    teal: "group-hover:text-teal-600",
    sky: "group-hover:text-sky-600",
    amber: "group-hover:text-amber-600",
    violet: "group-hover:text-violet-600",
};

const healthLinkedCardTitleHovers: Record<HealthLinkedCardTheme, string> = {
    teal: "group-hover:text-teal-500",
    sky: "group-hover:text-sky-500",
    amber: "group-hover:text-amber-600",
    violet: "group-hover:text-violet-500",
};

export const HEALTH_ECOSYSTEM_CARD_THEME: Record<string, HealthLinkedCardTheme> =
    {
        doctor: "teal",
        nurse: "amber",
        patient: "sky",
        hub: "violet",
        education: "violet",
        "medical-sensei": "teal",
        "business-sensei": "amber",
        "k12-sensei": "sky",
    };

/** Canonical ecosystem card order — doctor → nurse → patient → hub */
export const HEALTH_ECOSYSTEM_CARD_ORDER = [
    "doctor",
    "nurse",
    "patient",
    "hub",
] as const;

export function healthEcosystemCardSortIndex(cardId: string): number {
    const index = HEALTH_ECOSYSTEM_CARD_ORDER.indexOf(
        cardId as (typeof HEALTH_ECOSYSTEM_CARD_ORDER)[number]
    );
    return index === -1 ? HEALTH_ECOSYSTEM_CARD_ORDER.length : index;
}

export function sortHealthEcosystemCardsById<T extends { id?: unknown }>(
    cards: T[]
): T[] {
    return [...cards].sort(
        (a, b) =>
            healthEcosystemCardSortIndex(String(a.id ?? "")) -
            healthEcosystemCardSortIndex(String(b.id ?? ""))
    );
}

export function sortHealthEcosystemItems<T extends { id?: unknown; link?: unknown }>(
    items: T[]
): T[] {
    return [...items].sort((a, b) => {
        const aId =
            (a.id as string | undefined) ??
            healthCardIdFromPath((a.link as string) ?? "");
        const bId =
            (b.id as string | undefined) ??
            healthCardIdFromPath((b.link as string) ?? "");
        return healthEcosystemCardSortIndex(aId) - healthEcosystemCardSortIndex(bId);
    });
}

export function healthEcosystemCardThemeForId(
    cardOrGroupId: string
): HealthLinkedCardTheme {
    return HEALTH_ECOSYSTEM_CARD_THEME[cardOrGroupId] ?? "teal";
}

export function healthThemeFromHealthPath(path: string): HealthLinkedCardTheme {
    if (path.includes("aimma-nurse")) return "amber";
    if (path.includes("aimma-patient")) return "sky";
    if (path.includes("product-suite")) return "violet";
    return "teal";
}

export function healthCardIdFromPath(path: string): string {
    if (path.includes("aimma-nurse")) return "nurse";
    if (path.includes("aimma-doctor")) return "doctor";
    if (path.includes("aimma-patient")) return "patient";
    if (path.includes("product-suite")) return "hub";
    return "doctor";
}

export function healthLinkedCardHoverClasses(
    theme: HealthLinkedCardTheme
): string {
    return `${healthLinkedCardHoverBorders[theme]} ${healthLinkedCardHoverShadows[theme]}`;
}

/** Hover lift via translate — GPU layer promoted at rest to reduce text subpixel jitter. */
export const healthLinkedCardHoverLift =
    "transform-gpu translate-y-0 transition-[transform,box-shadow,border-color] duration-200 ease-out motion-reduce:transition-none motion-reduce:hover:translate-y-0 hover:-translate-y-0.5";

/** Stabilizes text inside transformed linked cards (paired with `healthLinkedCardHoverLift`). */
export const healthLinkedCardInnerStable =
    "antialiased [transform:translateZ(0)] [backface-visibility:hidden]";

const healthIntegrationEcosystemCardLinkedShared =
    "group relative flex h-full min-h-[14.5rem] cursor-pointer flex-col rounded-[1.25rem] border bg-white p-6 text-left no-underline shadow-[0_4px_20px_rgba(16,24,40,0.04)] transition-[box-shadow,border-color] duration-200 ease-out [text-align:left] hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ring-0 lg:p-7 lg:min-h-[16rem] [&_*]:cursor-pointer";

export function healthIntegrationEcosystemCardLinkedFor(
    cardId: string
): string {
    const theme = healthEcosystemCardThemeForId(cardId);
    return `${healthIntegrationEcosystemCardLinkedShared} ${healthLinkedCardHoverClasses(theme)} ${healthLinkedCardFocusOutlines[theme]}`;
}

export function healthIntegrationEcosystemCardCtaFor(
    theme: HealthLinkedCardTheme
): string {
    return `mt-5 inline-flex w-fit max-w-full items-center gap-1 text-left text-sm font-semibold text-grey-500 transition-colors duration-200 ease-out ${healthLinkedCardTextHovers[theme]} [text-align:left]`;
}

export function healthLegacyTileFor(theme: HealthLinkedCardTheme): string {
    return `group flex w-full min-w-0 min-h-[180px] flex-col gap-3 rounded-2xl border bg-white p-6 text-left shadow-[0_8px_24px_rgba(16,24,40,0.06)] transition-all duration-300 [text-align:left] hover:-translate-y-0.5 ring-0 ${healthLinkedCardHoverClasses(theme)}`;
}

export function healthLegacyTileTitleFor(theme: HealthLinkedCardTheme): string {
    return healthLinkedCardTitleHovers[theme];
}

export function healthLegacyTileBodyFor(theme: HealthLinkedCardTheme): string {
    return healthLinkedCardTitleHovers[theme];
}

export function healthProductGroupCardLinkedFor(
    groupId: string
): string {
    const theme = healthEcosystemCardThemeForId(groupId);
    return `group ${healthProductGroupCardBase} cursor-pointer [&_*]:cursor-pointer border bg-white no-underline ${healthLinkedCardHoverLift} hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ring-0 ${healthLinkedCardHoverClasses(theme)} ${healthLinkedCardFocusOutlines[theme]}`;
}

export function healthProductGroupCardHintFor(
    theme: HealthLinkedCardTheme
): string {
    return `mt-6 inline-flex items-center gap-1 text-left text-sm font-semibold text-grey-500 transition-colors duration-200 ease-out [text-align:left] ${healthLinkedCardTextHovers[theme]}`;
}

export function healthNurseIntegrationCardLinkedBorderFor(
    cardId: string
): string {
    return healthLinkedCardHoverBorders[healthEcosystemCardThemeForId(cardId)];
}

/** Preview-style integration cards (cta-secondary, child #integration) */
export const HEALTH_ECOSYSTEM_PREVIEW_CARD_TINTS: Record<string, string> = {
    doctor:
        "bg-[#eef9fa] shadow-[0_8px_28px_rgba(3,124,132,0.08)] hover:shadow-[0_8px_28px_rgba(16,24,40,0.06),0_28px_72px_rgba(3,124,132,0.18)]",
    nurse:
        "bg-amber-50/75 shadow-[0_8px_28px_rgba(217,119,6,0.1)] hover:shadow-[0_8px_28px_rgba(16,24,40,0.06),0_28px_72px_rgba(217,119,6,0.16)]",
    patient:
        "bg-sky-50/45 shadow-[0_8px_28px_rgba(14,116,144,0.06)]",
    hub: "bg-[#f3f0fb] shadow-[0_8px_28px_rgba(91,33,182,0.06)] hover:shadow-[0_8px_28px_rgba(16,24,40,0.06),0_28px_72px_rgba(91,33,182,0.14)]",
    education:
        "bg-[#f5f3ff] shadow-[0_8px_28px_rgba(91,33,182,0.06)]",
    "medical-sensei":
        "bg-[#eef9fa] shadow-[0_8px_28px_rgba(3,124,132,0.08)] hover:shadow-[0_8px_28px_rgba(16,24,40,0.06),0_28px_72px_rgba(3,124,132,0.18)]",
    "business-sensei":
        "bg-amber-50/75 shadow-[0_8px_28px_rgba(217,119,6,0.1)]",
    "k12-sensei":
        "bg-sky-50/45 shadow-[0_8px_28px_rgba(14,116,144,0.06)]",
};

export const HEALTH_ECOSYSTEM_PREVIEW_CARD_CTA: Record<string, string> = {
    doctor: "text-teal-700 group-hover:text-teal-800",
    nurse: "text-amber-700 group-hover:text-amber-800",
    patient: "text-sky-600",
    hub: "text-violet-700 group-hover:text-violet-800",
    education: "text-violet-700",
    "medical-sensei": "text-teal-700 group-hover:text-teal-800",
    "business-sensei": "text-amber-700",
    "k12-sensei": "text-sky-600",
};

export const healthEcosystemPreviewCardDisabledBorder =
    "border-dashed border-grey-200/90";

export function healthEcosystemPreviewCardTintFor(cardId: string): string {
    return (
        HEALTH_ECOSYSTEM_PREVIEW_CARD_TINTS[cardId] ??
        "bg-white shadow-[0_8px_28px_rgba(16,24,40,0.06)]"
    );
}

export function healthEcosystemPreviewCardCtaFor(cardId: string): string {
    return HEALTH_ECOSYSTEM_PREVIEW_CARD_CTA[cardId] ?? "text-teal-700";
}

/** lg-only 3-col — project Tailwind has sm + lg only (no md).
 * Preview mock layout: top-left anchor lg:left-[calc(52%+1rem)]; card overflow-hidden crop is intentional — see DECISION_LOG 2026-06 integration preview cards. */
export const healthIntegrationEcosystemPreviewCardGrid =
    "grid w-full min-w-0 grid-cols-1 items-stretch gap-5 text-left lg:grid-cols-3 lg:gap-6 [text-align:left]";

/** Hub alone on row 2 in 4-card preview grid — center with single-column width */
export const healthIntegrationPreviewHubGridItem =
    "h-full lg:col-span-3 lg:flex lg:justify-center";

export const healthIntegrationPreviewHubGridItemInner =
    "h-full w-full lg:w-[calc((100%-3rem)/3)]";

export const healthIntegrationPreviewCardRadius = "rounded-[1.35rem]";

/** Desktop preview cards — square floor; row stretch can grow taller for long copy */
export const healthIntegrationPreviewCardShell =
    "h-full w-full lg:aspect-square lg:h-auto lg:min-h-full";

const healthProductSuiteIconShells: Record<HealthLinkedCardTheme, string> = {
    teal: "bg-[rgba(3,124,132,0.08)]",
    sky: "bg-[rgba(14,116,144,0.08)]",
    amber: "bg-[rgba(217,119,6,0.1)]",
    violet: "bg-[rgba(91,33,182,0.08)]",
};

const healthProductSuiteIconGlyphs: Record<HealthLinkedCardTheme, string> = {
    teal: "text-teal-500",
    sky: "text-sky-600",
    amber: "text-amber-600",
    violet: "text-violet-600",
};

export function healthProductSuiteIconShellFor(
    theme: HealthLinkedCardTheme
): string {
    return healthProductSuiteIconShells[theme];
}

export function healthProductSuiteIconGlyphFor(
    theme: HealthLinkedCardTheme
): string {
    return healthProductSuiteIconGlyphs[theme];
}

export function healthLinkedCardTitleHoverFor(
    theme: HealthLinkedCardTheme
): string {
    return healthLinkedCardTitleHovers[theme];
}

export function healthLinkedCardFocusOutlineFor(
    theme: HealthLinkedCardTheme
): string {
    return `focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${healthLinkedCardFocusOutlines[theme]}`;
}

export function healthProductSuiteIconThemeForGroup(
    groupId: string
): HealthLinkedCardTheme {
    return healthEcosystemCardThemeForId(groupId);
}

/** Integration Ecosystem — product cards (aligns with Product Suite 2×2 + full-width row) */
export const healthIntegrationEcosystemCardGrid =
    "grid w-full min-w-0 grid-cols-1 gap-5 text-left sm:gap-6 lg:grid-cols-3 lg:items-stretch lg:gap-6 [text-align:left]";

export const healthIntegrationEcosystemCardBase =
    "relative flex h-full min-h-[14.5rem] flex-col rounded-[1.25rem] p-6 text-left sm:p-7 lg:min-h-[16rem] [text-align:left]";

export const healthIntegrationEcosystemCardLinked =
    healthIntegrationEcosystemCardLinkedFor("doctor");

/** Coming soon / non-interactive — muted disabled face, no hover lift */
export const healthIntegrationEcosystemCardDisabled =
    `${healthIntegrationEcosystemCardBase} cursor-default border border-dashed border-grey-200/90 bg-grey-50/45 shadow-none`;

export const healthIntegrationEcosystemCardHeader =
    "flex w-full items-start justify-between gap-3";

export const healthIntegrationEcosystemCardTitle =
    "mt-5 text-left text-lg font-bold leading-snug text-grey-900 [text-align:left]";

export const healthIntegrationEcosystemCardTitleDisabled =
    "text-grey-600";

export const healthIntegrationEcosystemCardBody =
    "mt-2.5 flex-1 text-left text-[0.9375rem] leading-[1.65] text-grey-600 [text-align:left]";

export const healthIntegrationEcosystemCardBodyDisabled =
    "text-grey-500";

/** CTA row inside linked card (not a nested link) */
export const healthIntegrationEcosystemCardCta =
    healthIntegrationEcosystemCardCtaFor("teal");

export const healthIntegrationEcosystemCardStatusDisabled =
    "inline-flex max-w-full cursor-default items-center rounded-full border border-dashed border-grey-300/80 bg-white/70 px-3 py-1 text-left text-xs font-medium text-grey-500 [text-align:left]";

/** Integration Ecosystem — deployment note (matches physician control / coordination disclaimer) */
export const healthIntegrationEcosystemDeploymentNote = healthPhysicianControlNote;
export const healthIntegrationEcosystemDeploymentNoteIcon =
    healthPhysicianControlNoteIcon;
export const healthIntegrationEcosystemDeploymentNoteText =
    healthPhysicianControlNoteText;

/** Agent block anchor target */
/* —— Typography (TitleSubtitle, TextBody, BusinessBanner, StepsCard) —— */

/** Section eyebrow — matches Healthcare Ecosystem reference */
export const healthSectionEyebrow =
    "text-left text-xs font-bold uppercase tracking-[0.2em] text-teal-500 [text-align:left]";

/**
 * Shared section H2 — matches site TitleSubtitle (`text-xl` = 30px in this project's scale).
 * Do not use text-2xl/sm:text-3xl here: sm triggers at 100px and 3xl = 60px on phones.
 */
export const healthSectionHeading =
    "min-w-0 max-w-full text-left text-xl font-semibold whitespace-pre-line text-grey-900 [text-align:left]";

export const healthSectionSubtitle =
    "text-left text-base leading-relaxed text-grey-500 max-w-3xl [text-align:left]";

export const healthSectionSubtitleWide =
    "text-left text-base leading-relaxed text-grey-500 max-w-[51rem] [text-align:left]";

/** Header block → section content (24px / 32px). Use mb-0 when parent uses healthSectionBlock gap. */
export const healthSectionHeaderBlock =
    "mb-6 w-full min-w-0 text-left lg:mb-8 [text-align:left]";

/** Eyebrow → title group (~12px) */
export const healthSectionHeaderContentGap =
    "flex w-full min-w-0 flex-col gap-3 text-left [text-align:left]";

/** Title → subtitle (~8–12px) */
export const healthSectionTitleBlockGap =
    "flex w-full min-w-0 flex-col gap-2 text-left [text-align:left]";

/**
 * Hero H1 — one step above section titles; lg matches BusinessBanner desktop (text-3xl = 60px).
 * Avoid sm:text-3xl so phones stay at text-2xl (40px), not 60px.
 */
export const healthHeroTitle =
    "text-left text-2xl font-bold whitespace-pre-line text-grey-800 lg:text-3xl lg:leading-tight";

export const healthHeroTitleAccent = "text-teal-500";

export const healthHeroSubtitle =
    "text-left text-base text-grey-400 leading-relaxed";

/** TitleSubtitle.title */
/** TitleSubtitle.subtitle / TextBody */
export const healthBody = "text-left text-base text-grey-400 leading-relaxed";

/** ToolCard2 title */
export const healthCardTitle =
    "text-left text-base font-bold leading-snug whitespace-pre-line lg:text-lg";

/** ToolCard2 / StepsCard body */
export const healthCardBody = "text-left text-sm text-grey-400";

/** StepsCard step title */
/* —— Surfaces (ToolCard2, MainFrame) —— */

/** Health landing hero — isolate + clip so ambient glow/shadows stop at breadcrumb (NavTree below) */
export const healthHeroSurface =
    "relative isolate w-full overflow-hidden bg-white";

/** Drugomics-style soft card elevation (no teal border on hover) */
/* —— Problem: insight tiles (not product cards) —— */

export const healthProblemEditorialImageFrame =
    "relative aspect-[4/3] w-full overflow-hidden rounded-[1.75rem] border border-grey-100 bg-[#f8fafc] shadow-[0_14px_34px_rgba(16,24,40,0.06)]";

export const healthProblemEditorialImage =
    "h-full w-full object-cover object-[center_34%]";

export const healthProblemEditorialFallback =
    "h-full w-full bg-[radial-gradient(ellipse_at_25%_20%,rgba(148,163,184,0.18),transparent_46%),radial-gradient(ellipse_at_78%_80%,rgba(100,116,139,0.12),transparent_50%),#f8fafc]";

export const healthProblemInsightCard =
    "flex w-full min-w-0 flex-col items-start justify-start border-b border-grey-200/80 pb-4 pr-1 pt-0.5 text-left [text-align:left] last:border-b-0 last:pb-0";

export const healthProblemInsightIndex =
    "block w-fit self-start text-left text-[10px] font-bold uppercase tracking-[0.16em] text-teal-500 [text-align:left]";

/* —— Aimma Doctor: hero bottom edge —— */

/** Soft teal fade at hero lower edge — z-[1]; covers right image, not copy/CTAs (z-[2]) */
export const healthAimmaDoctorHeroBottomFade =
    "pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-28 bg-[linear-gradient(to_bottom,rgba(255,255,255,0)_0%,rgba(248,251,252,0.45)_38%,rgba(248,251,252,0.88)_68%,rgba(236,247,248,1)_100%)] sm:h-36 lg:h-48";

/** Child-page hero — circular blurs clipped by header overflow */
export const healthChildPageHeroAmbientWrap =
    "pointer-events-none absolute inset-0 z-0 overflow-hidden";

/** White-only bottom clip into NavTree — no teal wash */
export const healthChildPageHeroBottomClip =
    "pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-14 bg-gradient-to-b from-transparent to-white sm:h-16 lg:h-20";

/** Patient hero — lifestyle backdrop (LT-10 heroBackdropRight) */
export const healthChildPageHeroBackdropWrap =
    "pointer-events-none absolute inset-0 z-0 overflow-hidden";

/** Mobile: bottom image band; desktop: right full-height panel */
export const healthChildPageHeroBackdropImage =
    "absolute bottom-0 left-0 right-0 z-0 h-60 w-full object-cover object-[right_24%] sm:h-[17rem] lg:inset-y-0 lg:left-auto lg:right-0 lg:h-full lg:w-[min(72%,820px)] lg:object-right lg:object-center";

/** Mobile: mask feather — 8rem overlap into image band (synced with image height) */
export const healthChildPageHeroBackdropFadeMobile =
    "pointer-events-none absolute inset-x-0 top-0 z-[1] bottom-28 bg-[linear-gradient(to_bottom,#ffffff_0%,#ffffff_38%,rgba(255,255,255,0.98)_52%,rgba(255,255,255,0.88)_66%,rgba(255,255,255,0.62)_80%,rgba(255,255,255,0.28)_92%,transparent_100%)] sm:bottom-36 lg:hidden";

export const healthChildPageHeroBackdropFadeDesktop =
    "pointer-events-none absolute inset-0 z-[1] hidden bg-[linear-gradient(to_right,rgba(255,255,255,1)_0%,rgba(255,255,255,0.94)_30%,rgba(255,255,255,0.72)_42%,rgba(255,255,255,0.28)_56%,transparent_72%)] lg:block";

/** Desktop only — soft clip into NavTree; mobile uses hard hero overflow clip */
export const healthChildPageHeroBottomClipDesktop =
    "pointer-events-none absolute inset-x-0 bottom-0 z-[2] hidden h-20 bg-gradient-to-b from-transparent to-white lg:block";

/* —— Aimma Doctor: Why section (2×2 reference layout) —— */

export const healthWhyDoctorSectionWrap =
    "relative w-full min-w-0";

export const healthWhyDoctorHeader =
    "relative z-[1] w-full min-w-0 max-w-[44rem] text-left [text-align:left]";

export const healthWhyDoctorHeaderStack =
    "flex w-full min-w-0 flex-col gap-0 text-left [text-align:left]";

export const healthWhyDoctorAccent =
    "mt-3 block h-0.5 w-11 rounded-full bg-teal-500/90";

export const healthWhyDoctorTitle =
    "mt-5 text-left text-[1.75rem] font-bold leading-[1.2] text-grey-900 [text-align:left] lg:text-[2.125rem] lg:leading-[1.15]";

export const healthWhyDoctorIntro =
    "mt-4 text-left text-base leading-relaxed text-grey-500 [text-align:left] lg:max-w-[40rem]";

export const healthWhyDoctorGrid =
    "relative z-[1] mt-10 grid w-full min-w-0 grid-cols-1 gap-7 lg:mt-12 lg:grid-cols-2 lg:gap-8";

export const healthWhyDoctorCard =
    "flex h-full w-full min-w-0 flex-col gap-4 rounded-[1.5rem] border border-[#E5E7EB] bg-white p-5 shadow-[0_10px_32px_rgba(16,24,40,0.05)] sm:p-6 lg:min-h-[12rem] lg:flex-row lg:items-stretch lg:gap-0 lg:rounded-[1.75rem] lg:p-7";

export const healthWhyDoctorCardIconCol =
    "flex shrink-0 items-center lg:items-center";

export const healthWhyDoctorIconShell =
    "flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#E8F6F7] sm:h-14 sm:w-14 lg:h-24 lg:w-24";

export const healthWhyDoctorIconGlyph =
    "h-6 w-6 text-teal-600 sm:h-7 sm:w-7 lg:h-11 lg:w-11";

export const healthWhyDoctorCardDivider =
    "hidden w-px shrink-0 self-stretch bg-[#E5E7EB] lg:mx-6 lg:block";

export const healthWhyDoctorCardContent =
    "flex min-w-0 flex-1 flex-col justify-center py-0.5 lg:pr-0.5";

export const healthWhyDoctorCardIndex =
    "text-left text-xs font-bold uppercase tracking-[0.14em] text-teal-600 [text-align:left]";

export const healthWhyDoctorCardTitle =
    "mt-1.5 text-left text-lg font-bold leading-snug text-grey-900 [text-align:left] lg:text-[1.125rem]";

export const healthWhyDoctorCardBody =
    "mt-2 text-left text-sm leading-relaxed text-grey-500 [text-align:left] lg:text-[0.9375rem] lg:leading-[1.65]";

/* —— Aimma Doctor: Clinical Journey timeline section —— */

export const healthClinicalJourneySectionWrap =
    "relative w-full min-w-0";

export const healthClinicalJourneyHeader =
    "relative z-[1] w-full min-w-0 max-w-[56rem] text-left [text-align:left]";

export const healthClinicalJourneyHeaderStack =
    "flex w-full min-w-0 flex-col text-left [text-align:left]";

export const healthClinicalJourneyAccent =
    "mt-3 block h-[3px] w-14 rounded-full bg-teal-500/90";

export const healthClinicalJourneyTitle =
    "mt-5 text-left text-[2rem] font-bold leading-[1.12] text-grey-900 [text-align:left] lg:text-[3rem] lg:leading-[1.08]";

export const healthClinicalJourneyIntro =
    "mt-4 text-left text-base leading-relaxed text-grey-500 [text-align:left] lg:max-w-[48rem] lg:text-lg lg:leading-[1.55]";

export const healthClinicalJourneyStageShell =
    "relative z-[1] mt-12 w-full min-w-0 lg:mt-14";

export const healthClinicalJourneyTimelineRail =
    "pointer-events-none absolute inset-x-0 top-[1.75rem] z-0 hidden -translate-y-1/2 items-center lg:flex";

export const healthClinicalJourneyTimelineEndDot =
    "h-2.5 w-2.5 shrink-0 rounded-full bg-teal-500";

export const healthClinicalJourneyTimelineLine =
    "relative mx-2 h-[1.5px] min-w-0 flex-1 bg-teal-300/85";

export const healthClinicalJourneyTimelineRailArrow =
    "absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-teal-400";

export const healthClinicalJourneyStageGrid =
    "relative z-[1] grid w-full min-w-0 grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-8";

export const healthClinicalJourneyStageColumn =
    "flex min-w-0 flex-col";

export const healthClinicalJourneyTimelineStage =
    "relative z-[1] hidden h-14 w-full items-center justify-center lg:flex";

export const healthClinicalJourneyTimelineNode =
    "relative z-[2] flex h-14 w-14 items-center justify-center rounded-full border-2 border-teal-400/90 bg-white text-[1rem] font-bold text-teal-600 shadow-[0_6px_18px_rgba(3,124,132,0.12)]";

export const healthClinicalJourneyTimelineConnector =
    "mx-auto hidden h-4 w-px border-l border-dashed border-teal-300/80 lg:block";

export const healthClinicalJourneyMobileStep =
    "mb-3 flex items-center gap-3 lg:hidden";

export const healthClinicalJourneyMobileNode =
    "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-teal-400/90 bg-white text-xs font-bold text-teal-600 shadow-[0_4px_14px_rgba(3,124,132,0.1)]";

export const healthClinicalJourneyCard =
    "flex w-full min-w-0 flex-col rounded-3xl border border-[#E5E7EB]/95 bg-white p-6 text-left shadow-[0_12px_36px_rgba(16,24,40,0.06)] [text-align:left] lg:p-7";

export const healthClinicalJourneyCardHeader =
    "flex w-full min-w-0 items-start gap-[1.125rem] text-left lg:gap-5 [text-align:left]";

export const healthClinicalJourneyCardCopy =
    "min-w-0 flex-1 text-left [text-align:left]";

export const healthClinicalJourneyCardIconShell =
    "flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#E8F6F7] lg:h-14 lg:w-14";

export const healthClinicalJourneyCardIconGlyph =
    "h-6 w-6 text-teal-600 lg:h-7 lg:w-7";

export const healthClinicalJourneyCardStageTitle =
    "text-left text-[1.25rem] font-semibold leading-[1.18] text-grey-900 [text-align:left] lg:text-[1.375rem] lg:leading-[1.2]";

export const healthClinicalJourneyCardSummary =
    "mt-1.5 text-left text-sm leading-[1.45] text-grey-500 [text-align:left]";

export const healthClinicalJourneyCardDivider =
    "mt-5 mb-4 h-px w-full bg-[#E5E7EB]/80";

export const healthClinicalJourneyChecklist =
    "mt-5 flex w-full min-w-0 flex-col gap-2.5 text-left [text-align:left]";

export const healthClinicalJourneyCheckItem =
    "flex w-full min-w-0 items-start gap-2.5 text-left [text-align:left]";

export const healthClinicalJourneyCheckIconWrap =
    "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-teal-500";

export const healthClinicalJourneyCheckIcon =
    "h-2.5 w-2.5 text-white";

export const healthClinicalJourneyCheckText =
    "text-left text-sm leading-[1.45] text-grey-700 [text-align:left]";

export const healthClinicalJourneyAgentChips =
    "flex w-full min-w-0 flex-wrap justify-start gap-2 text-left [text-align:left]";

export const healthClinicalJourneyAgentChipsGrid =
    "grid w-full min-w-0 grid-cols-2 justify-items-start gap-2 text-left [text-align:left]";

export const healthClinicalJourneyAgentChip =
    "inline-flex max-w-full min-h-[38px] items-center justify-start gap-2 self-start rounded-full bg-[rgba(3,124,132,0.08)] px-3.5 py-[5px] text-left text-[13px] font-medium leading-none text-grey-600 no-underline transition-colors hover:bg-[rgba(3,124,132,0.12)] hover:text-grey-700 [text-align:left]";

export const healthClinicalJourneyAgentChipIcon =
    "shrink-0 !h-5 !w-5 !text-teal-600 lg:!h-[1.375rem] lg:!w-[1.375rem]";

export const healthClinicalJourneyAgentChipLabel =
    "text-left leading-snug text-grey-600 [text-align:left]";

export const healthClinicalJourneyAgentSection =
    "w-full text-left [text-align:left]";

export const healthClinicalJourneyHubLinkWrap =
    "relative z-[1] mt-12 w-full text-left [text-align:left] lg:mt-14";

/** Secondary platform / suite link after module detail panel */
export const healthClinicalAgentsPlatformLinkWrap =
    "relative z-[1] mt-8 w-full text-left [text-align:left] lg:mt-10";

/* —— Aimma Doctor: Clinical Agents interactive section —— */

export const healthClinicalAgentsShell =
    "w-full min-w-0 text-left [text-align:left]";

export const healthClinicalAgentsTabRow =
    "relative -mb-3 mt-8 w-full min-w-0 overflow-visible text-left [text-align:left] lg:mt-10";

export const healthClinicalAgentsTabList =
    "flex w-full max-w-full snap-x snap-proximity gap-3 overflow-x-auto overscroll-x-contain scroll-smooth scroll-px-5 px-5 pt-1 pb-6 [-ms-overflow-style:none] [scrollbar-width:none] lg:gap-3.5 [&::-webkit-scrollbar]:hidden";

export const healthClinicalAgentsTab =
    "flex min-w-[13.5rem] snap-start shrink-0 flex-row items-center gap-2.5 rounded-2xl border px-3.5 py-2.5 text-left transition-[color,background-color,border-color,box-shadow] duration-200 [text-align:left] lg:min-w-[14.5rem] lg:gap-3 lg:px-4 lg:py-3";

export const healthClinicalAgentsTabActive =
    "border-teal-400/90 bg-teal-50 text-teal-600 shadow-[0_6px_20px_rgba(3,124,132,0.12)] ring-1 ring-teal-200/50";

export const healthClinicalAgentsTabInactive =
    "border-grey-100 bg-white text-grey-500 hover:border-teal-100 hover:bg-[rgba(3,124,132,0.02)] hover:text-grey-600";

export const healthClinicalAgentsTabContent =
    "flex min-w-0 flex-col items-start gap-0.5 text-left [text-align:left]";

export const healthClinicalAgentsTabNumber =
    "text-left text-[10px] font-bold tracking-[0.16em] text-inherit opacity-75 [text-align:left] lg:text-[11px]";

export const healthClinicalAgentsTabTitle =
    "min-w-0 text-left text-[0.875rem] font-bold leading-snug [text-align:left] lg:text-[0.9375rem]";

export const healthClinicalAgentsTabSubtitle =
    "min-w-0 text-left text-[11px] font-medium leading-snug text-grey-500 [text-align:left] lg:text-xs";

export const healthClinicalAgentsTabSubtitleActive =
    "text-teal-600/75";

export const healthClinicalAgentsActiveWrap =
    "relative mt-7 w-full min-w-0 lg:mt-9";

/** Active agent panel — single subtle card below tabs (not a section-wide wrapper) */
export const healthClinicalAgentsActivePanel =
    "w-full min-w-0 rounded-[1.875rem] bg-white px-5 py-7 text-left shadow-[0_24px_64px_rgba(16,24,40,0.1)] [text-align:left] sm:px-7 sm:py-8 lg:px-10 lg:py-10";

export const healthClinicalAgentsStageIntro =
    "w-full min-w-0 text-left [text-align:left]";

export const healthClinicalAgentsStageBody =
    "grid w-full min-w-0 grid-cols-1 gap-8 text-left [text-align:left] lg:grid-cols-2 lg:items-start lg:gap-8 xl:gap-10";

export const healthClinicalAgentsStageLeftCol =
    "flex w-full min-w-0 flex-col gap-6 text-left [text-align:left] lg:gap-7";

export const healthClinicalAgentsStageDetails =
    "w-full min-w-0 border-t border-grey-100/30 pt-8 text-left [text-align:left] lg:border-l lg:border-t-0 lg:border-grey-100/30 lg:pt-0 lg:pl-8 xl:pl-10";

export const healthClinicalAgentsSummaryPanel =
    "flex w-full min-w-0 flex-col gap-5 text-left [text-align:left] lg:gap-6";

export const healthClinicalAgentsStageImageWrap =
    "relative w-full overflow-hidden rounded-[1.5rem]";

export const healthClinicalAgentsStageImage =
    "block h-[16rem] w-full object-cover object-[center_10%] sm:h-[18rem] lg:h-[22rem] lg:max-h-[400px] xl:h-[24rem]";

export const healthClinicalAgentsStageLabel =
    "text-left text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500 [text-align:left]";

export const healthClinicalAgentsDetailTitle =
    "mt-2.5 text-left text-[1.625rem] font-bold leading-[1.12] text-grey-900 [text-align:left] lg:text-[2rem]";

export const healthClinicalAgentsDetailSummary =
    "mt-3 text-left text-base leading-[1.6] text-grey-500 [text-align:left] lg:text-[1.0625rem] lg:leading-[1.65]";

export const healthClinicalAgentsSummaryLabel =
    "text-left text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500 [text-align:left]";

export const healthClinicalAgentsIoSection =
    "w-full min-w-0 text-left [text-align:left]";

export const healthClinicalAgentsIoGrid =
    "grid w-full min-w-0 grid-cols-1 gap-5 text-left [text-align:left] lg:grid-cols-2 lg:gap-6 lg:divide-x lg:divide-grey-100/30";

export const healthClinicalAgentsIoBlock =
    "min-w-0 text-left [text-align:left] lg:pr-6 last:lg:pr-0 lg:[&:nth-child(2)]:pl-6";

export const healthClinicalAgentsIoBlockLabel =
    "text-left text-[11px] font-bold uppercase tracking-[0.16em] text-teal-600 [text-align:left]";

export const healthClinicalAgentsIoBlockLabelRow =
    "flex w-full min-w-0 items-center gap-2 text-left [text-align:left]";

export const healthClinicalAgentsIoList =
    "mt-3 flex w-full min-w-0 flex-col gap-2.5 text-left [text-align:left]";

export const healthClinicalAgentsIoItem =
    "flex w-full min-w-0 items-start justify-start gap-2.5 text-left text-[0.9375rem] leading-[1.5] text-grey-700 [text-align:left] lg:text-base lg:leading-[1.55]";

export const healthClinicalAgentsIoItemText =
    "block min-w-0 flex-1 text-left [text-align:left]";

export const healthClinicalAgentsIoCheck =
    "mt-1 flex h-[1.125rem] w-[1.125rem] shrink-0 items-center justify-center rounded-full bg-teal-600 text-white";

export const healthClinicalAgentsReviewHighlight =
    "flex w-full min-w-0 items-start gap-4 rounded-2xl border-2 border-teal-200/80 bg-teal-50/80 px-5 py-5 text-left shadow-[0_8px_28px_rgba(3,124,132,0.1)] [text-align:left] sm:px-6 sm:py-6";

export const healthClinicalAgentsReviewIcon =
    "mt-0.5 shrink-0 text-teal-600";

export const healthClinicalAgentsReviewLabel =
    "text-left text-[11px] font-bold uppercase tracking-[0.14em] text-teal-600 [text-align:left]";

export const healthClinicalAgentsReviewText =
    "mt-1.5 text-left text-[0.9375rem] leading-[1.55] text-grey-700 [text-align:left] lg:text-base lg:leading-[1.6]";

export const healthClinicalAgentsWorkflowNote =
    "relative w-full min-w-0 border-l-2 border-teal-200/50 py-1 pl-4 text-left [text-align:left]";

export const healthClinicalAgentsWorkflowLabel =
    "text-left text-[10px] font-bold uppercase tracking-[0.14em] text-grey-500 [text-align:left]";

export const healthClinicalAgentsWorkflowText =
    "mt-1 text-left text-sm leading-[1.55] text-grey-600 [text-align:left] lg:text-[0.9375rem]";

export const healthClinicalAgentsCapabilitiesBlock =
    "flex w-full min-w-0 flex-col gap-2 border-t border-grey-100/30 pt-4 text-left [text-align:left] lg:pt-5";

export const healthClinicalAgentsCapabilitiesLabel =
    "text-left text-[10px] font-bold uppercase tracking-[0.16em] text-grey-500 [text-align:left]";

export const healthClinicalAgentsTagsRow =
    "flex w-full min-w-0 flex-wrap items-center justify-start gap-2 text-left [text-align:left] lg:flex-nowrap lg:gap-2.5";

export const healthClinicalAgentsTag =
    "inline-flex max-w-full shrink-0 items-center rounded-full border border-grey-200/80 bg-white/90 px-2.5 py-0.5 text-left text-[11px] font-medium text-grey-600 [text-align:left] lg:whitespace-nowrap";

/* —— Solution: concept block + light pillar rows —— */

export const healthSolutionAimmaAccent =
    "mt-2 block h-0.5 w-12 rounded-full bg-teal-500/90";

export const healthSolutionMultiAgentCallout =
    "relative mt-5 pl-4 text-left before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:rounded-full before:bg-teal-500/85";

export const healthSolutionMultiAgentTitle =
    "text-sm font-semibold text-[#037C84] [text-align:left]";

export const healthSolutionDisclaimerRow =
    "mt-8 flex w-full min-w-0 flex-nowrap items-center justify-start gap-2.5 border-t border-grey-100/80 pt-5 text-left text-sm leading-relaxed text-grey-500 [text-align:left]";

export const healthSolutionDisclaimerIcon =
    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-teal-100/70 bg-teal-50/80 text-[#037C84]";

export const healthSolutionImageFrame =
    "relative aspect-[4/3] w-full overflow-hidden rounded-[1.4rem] shadow-[0_10px_24px_rgba(16,24,40,0.06)] lg:aspect-[5/4]";

export const healthSolutionImageSurface =
    "h-full w-full object-cover object-center";

export const healthSolutionCapabilitiesHeading = healthSectionEyebrow;

export const healthSolutionCapabilitiesGrid =
    "mt-4 grid w-full min-w-0 grid-cols-1 gap-4 text-left [text-align:left] md:grid-cols-2 lg:grid-cols-4";

export const healthSolutionCapabilityItemCard =
    "flex h-full w-full min-w-0 flex-col items-start justify-start rounded-2xl border border-grey-100/70 bg-[#fcfefe] px-5 py-5 text-left shadow-[0_6px_16px_rgba(16,24,40,0.045)] [text-align:left]";

export const healthSolutionCapabilityIconWrap =
    "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-50/65 text-[#037C84]";

export const healthSolutionCapabilityAccent =
    "mt-2 h-[2px] w-8 rounded-full bg-teal-400/80";

/* —— Shared visual asset placeholders (Problem / Solution / Architecture) —— */

export const healthLifecycleStageCardBase =
    "w-full rounded-2xl border p-5 text-left transition-all duration-200 sm:rounded-3xl sm:p-6";

export const healthLifecycleStageCardActive =
    "border border-teal-200/90 bg-white shadow-[0_8px_24px_rgba(3,124,132,0.08)] ring-1 ring-teal-500/15";

export const healthLifecycleStageCardInactive =
    "border-grey-200/90 bg-white hover:border-teal-200 hover:shadow-sm";

/** Timeline rail — line + dots share left-4 (16px); text offset via row pl-10 */
export const healthLifecycleTimelineRail =
    "relative ml-4 w-full min-w-0 shrink-0 self-start text-left [text-align:left]";

/** Vertical line on shared axis (same left-4 + -translate-x-1/2 as dots) */
export const healthLifecycleTimelineLine =
    "pointer-events-none absolute bottom-0 left-4 top-0 w-px -translate-x-1/2 bg-teal-300/60";

export const healthLifecycleSelectorBase =
    "relative mb-5 block w-full pl-10 text-left [text-align:left] transition-colors duration-200 last:mb-0";

export const healthLifecycleSelectorActive = "text-teal-500";

export const healthLifecycleSelectorInactive =
    "text-grey-400 hover:text-teal-400";

/** Dot on row: same left-4 axis as line; does not affect text flow */
export const healthLifecycleSelectorDot =
    "absolute left-4 top-2 z-[1] flex h-4 w-4 -translate-x-1/2 rounded-full border-2 border-white transition";

export const healthLifecycleSelectorDotActive = "scale-110 bg-teal-500";

export const healthLifecycleSelectorDotInactive = "bg-teal-100";

export const healthLifecycleSelectorLabel =
    "text-left text-xs font-semibold tracking-wide [text-align:left]";

export const healthLifecycleSelectorTitle =
    "mt-1 text-left text-lg font-semibold leading-snug [text-align:left]";

/** Scroll-distance spacer only — desktop lg+ (hidden below lg) */
export const healthLifecycleScrollTrack =
    "relative hidden w-full min-w-0 lg:block";

/**
 * Full pinned lifecycle scene — viewport frame, opaque surface, internal stage switching.
 */
export const healthLifecycleStickyScene =
    "sticky top-[var(--health-nav-offset,5rem)] z-[1] flex w-full min-w-0 max-w-full flex-col justify-start bg-white [text-align:left] min-h-[calc(100svh-var(--health-nav-offset,5rem))]";

/** Content inside pinned scene — matches compact HealthSection rhythm */
export const healthLifecycleSceneInner =
    `mx-auto flex w-full min-w-0 max-w-[1072px] flex-col items-start text-left [text-align:left] ${healthContentPadX} ${healthSectionYCompact} ${healthSectionBlock}`;

/** Compact heading block inside pinned scene */
export const healthLifecycleSceneHeading =
    "w-full min-w-0 text-left [text-align:left]";

/** Vertical stage list inside rail */
export const healthLifecycleTimelineList =
    "w-full min-w-0 text-left [text-align:left]";

/** Desktop lifecycle grid — selector top-aligned with detail card */
export const healthLifecycleStickyGrid =
    "grid w-full min-w-0 items-start gap-5 text-left [text-align:left] lg:grid-cols-[minmax(0,0.24fr)_minmax(0,0.76fr)] lg:gap-8";

/** Right column — detail card */
export const healthLifecyclePanelColumn =
    "flex w-full min-w-0 self-start lg:items-stretch";

/** Mobile panel host — overflow-x-hidden clips box-shadow (forces overflow-y: auto); pad for shadow bleed */
export const healthLifecycleMobilePanelWrap =
    "relative w-full min-w-0 max-w-full overflow-visible px-1 pb-6 -mb-2";

export const healthLifecycleDetailCardSticky =
    "w-full";

/** Detail panel — journey scene card (no inner scroll) */
export const healthLifecycleDetailCard =
    "flex w-full flex-col rounded-[1.5rem] border border-teal-100/70 bg-white p-5 text-left shadow-[0_16px_48px_rgba(3,124,132,0.09)] sm:rounded-[2rem] sm:p-7 lg:rounded-[2.5rem] lg:p-10";

/** Stage-specific care scene placeholder — image slot, not progress UI */
export const healthLifecycleStageSceneFrame =
    "relative w-full overflow-hidden rounded-2xl border border-teal-100/50 bg-[radial-gradient(ellipse_at_28%_18%,rgba(87,196,203,0.18),transparent_52%),radial-gradient(ellipse_at_85%_75%,rgba(3,124,132,0.08),transparent_48%),#fafcfd] min-h-[8.5rem] sm:min-h-[10rem]";

export const healthLifecycleJourneyNotes =
    "mt-4 w-full space-y-2 text-left [text-align:left]";

export const healthLifecycleJourneyNote =
    "relative pl-4 text-left text-sm leading-relaxed text-grey-500 before:absolute before:left-0 before:top-[0.55em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-teal-400/75 [text-align:left]";

/** Shared focus ring — Health landing interactive controls */
export const healthFocusVisible =
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500";

/** Primary teal tag — marketing / context pills (e.g. Ecosystem examples) */
export const healthChipTealTag =
    "inline-flex max-w-full items-center rounded-full border border-teal-200/60 bg-teal-50/80 px-3 py-1 text-left text-xs font-medium text-teal-700 [text-align:left]";

/** Secondary neutral reference chip — lifecycle links to Product Suite */
export const healthChipReferenceNeutral =
    `inline-flex max-w-full cursor-pointer items-center rounded-full border border-grey-200/90 bg-white px-2.5 py-1 text-left text-xs font-medium text-grey-600 transition-colors hover:border-teal-300/70 hover:bg-teal-50/25 hover:text-teal-600 ${healthFocusVisible} [text-align:left]`;

/** Secondary product references — not suite CTAs */
export const healthLifecycleChipsRegion =
    "mt-5 w-full border-t border-grey-100/90 pt-4 text-left [text-align:left]";

/** @deprecated Use healthChipReferenceNeutral */
export const healthLifecycleChipReference = healthChipReferenceNeutral;

export const healthLifecycleChipDeveloping =
    "inline-flex max-w-full cursor-default items-center rounded-full border border-dashed border-grey-300/80 bg-transparent px-2.5 py-1 text-xs font-medium text-grey-500";

export const healthLifecycleChipReferenceStatic =
    "inline-flex max-w-full cursor-default items-center rounded-full border border-grey-200/80 bg-white/80 px-2.5 py-1 text-xs font-medium text-grey-500";

/* —— Buttons (ContactUsButton, MoreButton) —— */

/** Arrow icon inside `group` button/link — slides right on hover; transform only (no color transition). */
export const healthButtonArrow =
    "inline-flex shrink-0 items-center justify-center self-center leading-none transition-transform duration-200 ease-out group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0";

/** Font Awesome arrow — buttons / section CTAs (default size). */
export const healthButtonArrowFa = `h-[0.9em] w-[0.9em] max-h-4 max-w-4 ${healthButtonArrow}`;

/** Font Awesome arrow — compact card inline CTAs (sizes with parent text). */
export const healthButtonArrowFaSm = `h-[0.85em] w-[0.85em] max-h-3.5 max-w-3.5 ${healthButtonArrow}`;

/** Button label — fixed width slot so arrow motion does not nudge text. */
export const healthButtonLabel = "shrink-0";

export const healthPrimaryButton =
    "group inline-flex w-full lg:w-auto items-center justify-center gap-2 rounded-md border-2 border-grey-500 bg-grey-500 px-8 py-2 text-base text-white antialiased transform-gpu min-h-[44px] hover:text-teal-300";

export const healthSecondaryButton =
    "group inline-flex w-full lg:w-auto items-center justify-center gap-2 rounded-md px-3 py-2 text-base font-bold text-teal-500 antialiased transform-gpu transition-[background-color] duration-200 ease-out hover:bg-teal-100/50 min-h-[44px]";

/** JoinUsSection inner spacing (background on HealthSection) */
export const healthCtaBand = "w-full py-4 lg:py-6";

export const healthCtaTitle = "text-left text-xl font-semibold text-white lg:text-center";

export const healthCtaSubtitle = "text-left text-base font-light text-white lg:text-center";

export const healthCtaButton =
    "group inline-flex w-full lg:w-auto items-center justify-center gap-2 rounded-md border-2 border-grey-500 bg-grey-500 px-12 py-2 text-base text-white antialiased transform-gpu min-h-[44px] hover:text-teal-300";

export const healthCtaSecondaryButton =
    "group inline-flex w-full lg:w-auto items-center justify-center gap-2 rounded-md border border-white/35 px-12 py-2 text-base text-white antialiased transform-gpu transition-[background-color] duration-200 ease-out hover:bg-white/10 min-h-[44px]";

/** Tertiary text link on teal CTA band */
/** Hero product-line label (sentence case — not section eyebrow) */
export const healthProductLineLabel =
    "text-left text-sm font-semibold text-teal-600 [text-align:left]";

/* —— Pills / chips (UnitBadge-adjacent, lifecycle tabs) —— */

/** Stakeholder tab panel — light surface, not product catalog */
/* —— Deployment: process steps (typographic, not heavy cards) —— */

/* —— Secondary / legacy deep-link tiles —— */

export const healthLegacyTile =
    "group flex w-full min-w-0 min-h-[180px] flex-col gap-3 rounded-2xl border border-grey-100 bg-white p-6 text-left shadow-[0_8px_24px_rgba(16,24,40,0.06)] transition-all duration-300 [text-align:left] hover:-translate-y-0.5 hover:shadow-back";

/* —— Platform: intelligence / architecture layer —— */

/** Under-development lifecycle chips — product name only, not product links */
/* —— LeftBorderCard pattern —— */
/* —— Product Suite (2×2 audience groups) —— */

export const healthProductSuiteGrid =
    "grid w-full min-w-0 grid-cols-1 gap-5 text-left [text-align:left] sm:gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-7";

/** Paired-row group cards (rows 1–2) — equal height within row on lg+ only */
export const healthProductSuiteGroupStretch = "min-h-0 lg:h-full";

export const healthProductSuiteGroupFullWidth = "w-full min-w-0 lg:col-span-2";

const healthProductGroupCardBase =
    "flex w-full min-w-0 flex-col rounded-[1.25rem] bg-white p-6 text-left shadow-[0_12px_40px_rgba(16,24,40,0.06)] [text-align:left] lg:rounded-[1.5rem] lg:p-7";

/** Cross-BU Educator bridge — neutral gray gradient (distinct from linked-card teal hover) */
export const healthProductGroupCardBridge =
    `${healthProductGroupCardBase} cursor-default border border-grey-200/80 bg-[linear-gradient(145deg,#eceef2_0%,#f6f7f9_48%,#ffffff_100%)]`;

/** Educator bridge card when linked — same hover lift as `healthProductGroupCardLinked`, keeps gray gradient. */
export const healthProductGroupCardBridgeLinked =
    `group ${healthProductGroupCardBase} cursor-pointer [&_*]:cursor-pointer border border-grey-200/80 bg-[linear-gradient(145deg,#eceef2_0%,#f6f7f9_48%,#ffffff_100%)] no-underline ring-1 ring-transparent ${healthLinkedCardHoverLift} hover:border-teal-300/65 hover:shadow-[0_8px_28px_rgba(16,24,40,0.07),0_32px_80px_rgba(3,124,132,0.22)] hover:ring-teal-100/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500`;

export const healthProductGroupBridgeEyebrow =
    "text-left text-[11px] font-bold uppercase tracking-[0.14em] text-grey-600 [text-align:left]";

export const healthProductGroupBridgeBody =
    "mt-2 text-left text-sm leading-relaxed text-grey-500 [text-align:left]";

/** Nursing Robot — roadmap / future extension */
export const healthProductGroupCardDeveloping =
    `${healthProductGroupCardBase} border border-dashed border-grey-200/90 bg-[linear-gradient(135deg,#fafcfd_0%,#ffffff_70%)]`;

export const healthProductGroupHeader =
    "flex w-full min-w-0 items-start gap-4";

export const healthProductGroupHeaderText =
    "min-w-0 flex-1";

export const healthProductGroupTitle =
    "text-left text-lg font-bold leading-snug text-grey-800 [text-align:left] lg:text-xl";

/**
 * Linked group card — teal border + shadow lift; card face stays white (no gradient wash).
 * Product Suite only — not themed per audience group.
 */
export const healthProductGroupCardLinked =
    `group ${healthProductGroupCardBase} cursor-pointer [&_*]:cursor-pointer border border-grey-100/80 ring-1 ring-transparent bg-white no-underline ${healthLinkedCardHoverLift} hover:border-teal-300/65 hover:bg-white hover:shadow-[0_8px_28px_rgba(16,24,40,0.07),0_32px_80px_rgba(3,124,132,0.22)] hover:ring-teal-100/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500`;

/** Non-linked care-delivery groups — premium at rest */
export const healthProductGroupCardStatic =
    `${healthProductGroupCardBase} cursor-default border border-grey-100/50`;

/** Text hint inside linked card (not a nested link). Arrow uses `healthButtonArrowFaSm`. */
export const healthProductGroupCardHint =
    "mt-6 flex w-full items-center justify-start gap-1.5 self-start text-left text-sm font-semibold leading-none text-grey-500 [text-align:left] group-hover:text-teal-600";

/** Pinned to card bottom when paired-row stretch makes the card taller than its content */
export const healthProductGroupCardHintPinned =
    "mt-auto flex w-full items-center justify-start gap-1.5 self-start pt-6 text-left text-sm font-semibold leading-none text-grey-500 [text-align:left] group-hover:text-teal-600";

/** Linked card inner — fill stretched row height so footer hint can sit at bottom */
export const healthProductGroupLinkedInner =
    "flex h-full min-h-0 w-full min-w-0 flex-col items-start text-left [text-align:left]";

/** Doctor grid — 8th cell beside the last module; left-aligned, vertically centered */
export const healthProductGroupCardHintDoctorCell =
    "flex h-full min-h-[4rem] w-full self-stretch items-center justify-start lg:min-h-0";

export const healthProductGroupCardHintDoctor =
    "flex shrink-0 items-center justify-start gap-1.5 text-left text-sm font-semibold leading-none text-grey-500 group-hover:text-teal-600";

export const healthProductGroupProductList =
    "mt-5 w-full min-w-0 space-y-2.5 lg:mt-6";

export const healthProductGroupProductListCompact =
    "mt-4 w-full min-w-0 space-y-2";

/** Doctor group — full-width card with two-column module grid on lg+ */
export const healthProductGroupProductListDoctorGrid =
    "mt-4 grid w-full min-w-0 grid-cols-1 gap-2 lg:mt-5 lg:grid-cols-2 lg:gap-x-4 lg:gap-y-2";

export const healthProductCapabilityRow =
    "flex w-full min-w-0 items-start gap-3 rounded-xl bg-[rgba(3,124,132,0.03)] px-4 py-4 text-left [text-align:left] sm:gap-4 sm:py-[18px]";

export const healthProductCapabilityRowCompact =
    "flex w-full min-w-0 items-start gap-3 rounded-lg bg-[rgba(3,124,132,0.03)] px-3.5 py-3 text-left [text-align:left] sm:gap-3.5";

export const healthProductCapabilityContent =
    "min-w-0 flex-1";

export const healthProductCapabilityTitleRow =
    "flex min-h-[1.25rem] flex-wrap items-center gap-x-2 gap-y-1";

export const healthProductCapabilityTitleCompact =
    "text-left text-[13px] font-bold leading-snug text-grey-800 [text-align:left]";

export const healthProductCapabilityDescCompact =
    "mt-1 text-left text-xs leading-relaxed text-grey-500 [text-align:left]";

export const healthProductCapabilityTitle =
    "text-left text-sm font-bold leading-snug text-grey-800 [text-align:left]";

export const healthProductCapabilityDesc =
    "mt-1.5 text-left text-sm leading-relaxed text-grey-500 [text-align:left]";

